/**
 * "Gửi yêu cầu chỉnh sửa" — `POST /api/v1/attendance/corrections`, Task 28's
 * workflow, and spec 01 §17's *"Employee gửi yêu cầu chỉnh sửa"*.
 *
 * It creates a **request**, never an edit. `openapi.yaml` says so and the schema
 * enforces it: raw activity is untouched (G6), approval writes a separate adjustment
 * row, and the copy here says that rather than implying the day is being rewritten.
 *
 * ── What this form does not send, and why ──────────────────────────────────
 *
 * **No `employee_id`.** The field exists in the schema so a client with the wrong
 * employee loaded gets a `403` instead of filing under somebody else's name; omitting
 * it means "for myself", which is the only thing this screen ever files.
 * `assertSubjectIsSelf` refuses anything else whatever the caller holds, because an
 * explanation is that person's own account of their day.
 *
 * **No `requested_adjustment_minutes`.** The interval *is* the claim (Task 28
 * amended the contract for exactly this reason: two statements of one quantity that a
 * client could make disagree). The response reports `requested_minutes` so nothing
 * has to be subtracted here.
 *
 * ── The refusals are the interesting part ──────────────────────────────────
 *
 * Every bound this endpoint applies is rendered from the server's own answer:
 * `WORKFORCE_VALIDATION_FAILED` for an interval longer than 31 days, an end not after
 * the start, or a `reason_code` of `other` with fewer than 10 characters of note;
 * `WORKFORCE_SCOPE_DENIED` for a subject that is not the caller. The form applies one
 * client-side check only — `reason_note` at least three characters, which is the
 * schema's own `minLength` and is arithmetic rather than policy.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { formatDay, formatInstant, wallClockToInstant } from '../../i18n/datetime';
import type { MessageKey } from '../../i18n/messages';
import { ATTENDANCE_CORRECTIONS_PATH } from '../../api/paths';
import {
  EXPLANATION_REASON_CODES,
  type AttendanceCorrection,
  type AttendanceCorrectionCreateRequest,
  type ExplanationReasonCode,
} from '../../api/types';
import { useApiMutation } from '../../api/useApiMutation';
import { ApiErrorNote } from '../../states/ApiErrorNote';
import { StatusNote } from '../../states/StatusNote';

const REASON_KEY: Readonly<Record<ExplanationReasonCode, MessageKey>> = {
  meeting: 'correction.reason.meeting',
  phone_call: 'correction.reason.phone_call',
  reading_document: 'correction.reason.reading_document',
  paperwork: 'correction.reason.paperwork',
  client_visit: 'correction.reason.client_visit',
  break: 'correction.reason.break',
  offsite_work: 'correction.reason.offsite_work',
  agent_down: 'correction.reason.agent_down',
  device_issue: 'correction.reason.device_issue',
  power_outage: 'correction.reason.power_outage',
  approved_leave: 'correction.reason.approved_leave',
  other: 'correction.reason.other',
};

const STATUS_KEY: Readonly<Record<AttendanceCorrection['status'], MessageKey>> = {
  pending_manager: 'correction.status.pending_manager',
  pending_hr: 'correction.status.pending_hr',
  approved: 'correction.status.approved',
  rejected: 'correction.status.rejected',
  withdrawn: 'correction.status.withdrawn',
};

/** `reason_note` is `minLength: 3` on the wire. Arithmetic, not policy — so it is here. */
const MIN_NOTE = 3;

export interface CorrectionFormProps {
  readonly date: string;
  readonly timezone: string;
  readonly onClose: () => void;
  /** Called after a successful file, so the attendance card re-reads. */
  readonly onFiled: () => void;
}

export function CorrectionForm({
  date,
  timezone,
  onClose,
  onFiled,
}: CorrectionFormProps): ReactNode {
  const { t, locale } = useI18n();
  const [reason, setReason] = useState<ExplanationReasonCode>('agent_down');
  const [startWall, setStartWall] = useState(`${date}T09:00`);
  const [endWall, setEndWall] = useState(`${date}T17:00`);
  const [note, setNote] = useState('');

  const mutation = useApiMutation<AttendanceCorrectionCreateRequest, AttendanceCorrection>(
    ATTENDANCE_CORRECTIONS_PATH,
  );

  // Focus lands on the outcome — the confirmation or the refusal — because that is
  // the answer the person came for, and on a refusal it is where the server's own
  // boundary is written. `role="alert"` announces it to a screen reader; moving
  // focus is what puts a keyboard user's next Tab *after* it instead of back at the
  // top of a form they have finished with.
  const outcomeRef = useRef<HTMLDivElement | null>(null);
  const settled = mutation.status === 'ok' || mutation.status === 'error';
  useEffect(() => {
    if (settled) outcomeRef.current?.focus();
  }, [settled]);

  const startedAt = wallClockToInstant(startWall, timezone);
  const endedAt = wallClockToInstant(endWall, timezone);
  const ready = startedAt !== null && endedAt !== null && note.trim().length >= MIN_NOTE;

  return (
    <div className="wp-card" data-testid="correction-form">
      <h3 className="wp-section__title">
        {t('correction.heading', {
          date: formatDay(date, locale, { day: 'numeric', month: 'long', year: 'numeric' }),
        })}
      </h3>
      <p className="wp-small wp-muted">{t('correction.intro')}</p>

      <form
        className="wp-stack"
        onSubmit={(event) => {
          event.preventDefault();
          // `ready` is an aliased condition over both instants, so TypeScript narrows
          // them here. Re-checking would be dead code the linter is right to refuse.
          if (!ready) return;
          void mutation
            .submit({
              date,
              reason_code: reason,
              reason_note: note.trim(),
              requested_start_at: startedAt,
              requested_end_at: endedAt,
            })
            .then(onFiled);
        }}
      >
        <div className="wp-field">
          <label htmlFor="correction-reason">{t('correction.reason')}</label>
          <select
            id="correction-reason"
            value={reason}
            onChange={(event) => {
              // The twelve values are a closed CHECK constraint, so the option list is
              // the enum and a value outside it is a constraint violation rather than a
              // customisation. The cast is safe because every option came from that list.
              setReason(event.target.value as ExplanationReasonCode);
            }}
            data-testid="correction-reason"
          >
            {EXPLANATION_REASON_CODES.map((code) => (
              <option key={code} value={code}>
                {t(REASON_KEY[code])}
              </option>
            ))}
          </select>
        </div>

        <div className="wp-field">
          <label htmlFor="correction-start">{t('correction.start')}</label>
          <input
            id="correction-start"
            type="datetime-local"
            value={startWall}
            onChange={(event) => {
              setStartWall(event.target.value);
            }}
            data-testid="correction-start"
          />
        </div>

        <div className="wp-field">
          <label htmlFor="correction-end">{t('correction.end')}</label>
          <input
            id="correction-end"
            type="datetime-local"
            value={endWall}
            onChange={(event) => {
              setEndWall(event.target.value);
            }}
            data-testid="correction-end"
          />
        </div>

        <div className="wp-field">
          <label htmlFor="correction-note">{t('correction.note')}</label>
          <input
            id="correction-note"
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
            data-testid="correction-note"
          />
          <p className="wp-small wp-muted">
            {reason === 'other'
              ? t('correction.note.other')
              : t('correction.note.hint', { min: MIN_NOTE })}
          </p>
        </div>

        {startedAt === null || endedAt === null ? null : (
          <p className="wp-small wp-muted" data-testid="correction-resolved">
            {t('correction.resolved', {
              from: formatInstant(startedAt, locale, timezone),
              to: formatInstant(endedAt, locale, timezone),
            })}
          </p>
        )}

        <div className="wp-note__actions">
          <button
            type="submit"
            className="wp-button wp-button--primary"
            disabled={!ready || mutation.status === 'pending'}
            data-testid="correction-submit"
          >
            {mutation.status === 'pending' ? t('correction.submitting') : t('correction.submit')}
          </button>
          <button
            type="button"
            className="wp-button"
            onClick={onClose}
            data-testid="correction-cancel"
          >
            {t('correction.cancel')}
          </button>
        </div>
      </form>

      <div ref={outcomeRef} tabIndex={-1} data-testid="correction-outcome">
        {mutation.status === 'ok' && mutation.data !== null ? (
          <StatusNote
            severity="low"
            role="alert"
            title={t('correction.filed.title')}
            testId="correction-filed"
          >
            <p>{t('correction.filed.body')}</p>
            <p className="wp-small wp-muted">
              {t('correction.filed.status', { status: t(STATUS_KEY[mutation.data.status]) })}
              {mutation.data.requires_hr_approval === true ? ` · ${t('correction.filed.hr')}` : ''}
            </p>
          </StatusNote>
        ) : null}

        {mutation.error === null ? null : (
          <ApiErrorNote
            error={mutation.error}
            subject={t('correction.subject')}
            testId="correction-error"
          />
        )}
      </div>
    </div>
  );
}
